/* eslint-disable promise/prefer-await-to-then */
import { IconButton, Spinner, Stack, TooltipHost } from "@fluentui/react"
import { useCallback, useEffect, useState } from "react"
import { getEnrollementRecordsFromHouseholdMembers, IS_ADMIN } from "../lib/helper"
import { makeACMGCRMApiCall } from "../services/apiService"
import { IAccount } from "../types/IAccount"
import { IEnrollmentRecord } from "../types/IEnrollmentRecord"
import { IHouseholdMemberEnrollmentManager } from "../types/IHouseholdMember"

interface ICensusCountProps {
	account: IAccount
	setShowEnrollmentManagerDialog: (item: IAccount) => void
	setShowUploadCensusDialog: (item: IAccount) => void
}

export function CensusCount(props: ICensusCountProps): JSX.Element {
	const { account, setShowEnrollmentManagerDialog, setShowUploadCensusDialog } = props
	const [employees, setEmployees] = useState<IEnrollmentRecord[]>([])
	const [employeeEnrollments, setEmployeeEnrollments] = useState<IEnrollmentRecord[]>([])
	const [loadingEmployees, setLoadingEmployees] = useState<boolean>(true)
	const isProspect = account.cw_accounttype.includes("Prospect")

	const getHouseholdMembers = useCallback(async (): Promise<IHouseholdMemberEnrollmentManager[]> => {
		//const escapedFetchXML = encodeURIComponent(fetchXml)
		const apiUrl = `getHouseholdMembers?accountId=${account.accountid}`

		try {
			const res = await makeACMGCRMApiCall(apiUrl)
			const results = res as IHouseholdMemberEnrollmentManager[]
			const filteredResults: IHouseholdMemberEnrollmentManager[] = []

			results.forEach(r => {
				const foundIndex = filteredResults.findIndex(f => f.cw_householdmembersid === r.cw_householdmembersid)
				if (foundIndex === -1) {
					filteredResults.push(r)
				}
			})

			return filteredResults
		} catch (error) {
			console.error("Error fetching household members:", error)
			throw error
		}
	}, [account.accountid])

	useEffect(() => {
		setLoadingEmployees(true)
		const fetchData = async () => {
			try {
				if (account) {
					const householdMembers = await getHouseholdMembers()
					const innerEnrollments = getEnrollementRecordsFromHouseholdMembers(householdMembers)
					setEmployees(innerEnrollments)
					setEmployeeEnrollments(innerEnrollments.filter(e => e.relationship === "Employee"))
				}
			} catch (err) {
				console.log(err)
			} finally {
				setLoadingEmployees(false)
			}
		}
		fetchData()
	}, [account, getHouseholdMembers])

	return (
		<>
			{loadingEmployees && <Spinner />}
			{!loadingEmployees && employees.length > 0 ? (
				<Stack verticalAlign="center" horizontal>
					<TooltipHost content={`${employees.length} household members and ${employeeEnrollments.length} employees`}>{employees.length}</TooltipHost>
					{(isProspect || IS_ADMIN) && (
						<TooltipHost content="Click here to manage the enrollment">
							<IconButton iconProps={{ iconName: "People" }} onClick={() => setShowEnrollmentManagerDialog(account)} />
						</TooltipHost>
					)}
				</Stack>
			) : (
				!loadingEmployees &&
				employees.length === 0 && (
					<Stack verticalAlign="center" horizontal>
						<span>0</span>
						{IS_ADMIN ||
							(!account.cw_lockedcensus && !account.cw_lockunderwriting && !account.cw_salelost && isProspect && (
								<TooltipHost content="Click here to upload the census">
									<IconButton iconProps={{ iconName: "PeopleAdd" }} onClick={() => setShowUploadCensusDialog(account)} />
								</TooltipHost>
							))}
					</Stack>
				)
			)}
		</>
	)
}
